import React, { useEffect, useState, useCallback } from "react";
import { API_URL } from "./config";
import { useToast } from "./Toast";

// Matches the shape returned by GET /me/cross-guild-invitations
export type CrossGuildInvite = {
  id: string;
  guild_raid_id: string;
  character_id: string;
  character_name: string;
  class_name: string | null;
  guild_id: string;
  guild_name: string;
  guild_tag: string | null;
  guild_logo_url: string | null;
  raid_name: string;
  raid_type_label: string | null;
  raid_image_url: string | null;
  starts_at: string | null;
  loot_info_sent: string | null;
  notified_at: string | null;
  created_at: string;
  response: "accepted" | "declined" | null;
};

const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", Shaman: "#0070DE", Mage: "#69CCF0", Warlock: "#9482C9",
  Druid: "#FF7D0A", "Death Knight": "#C41F3B",
};

function formatStartsAt(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
}

function resolveLogo(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_URL}${url}`;
}

/**
 * Renders on the Home page for players who have been invited to raid with
 * another guild (cross-guild notices). Does NOT render when the list is empty.
 *
 * Props:
 *   isAuthed    — only fetch once the user is signed in
 *   onResponded — optional refresh hook (bump dataVersion in parent)
 *   variant     — "desktop" | "mobile", controls typography/padding
 */
export default function CrossGuildInvitesHome({
  isAuthed,
  onResponded,
  variant = "desktop",
}: {
  isAuthed: boolean;
  onResponded?: () => void;
  variant?: "desktop" | "mobile";
}) {
  const { showToast } = useToast();
  const [invites, setInvites] = useState<CrossGuildInvite[]>([]);
  const [loadingById, setLoadingById] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!isAuthed) { setInvites([]); return; }
    try {
      const res = await fetch(`${API_URL}/me/cross-guild-invitations`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setInvites(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [isAuthed]);

  useEffect(() => { load(); }, [load]);

  const respond = useCallback(async (inv: CrossGuildInvite, response: "accepted" | "declined") => {
    setLoadingById(prev => ({ ...prev, [inv.id]: true }));
    try {
      const res = await fetch(
        `${API_URL}/me/cross-guild-invitations/${encodeURIComponent(inv.id)}/respond`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ response }),
        }
      );
      if (!res.ok) throw new Error("respond_failed");
      setInvites(prev => prev.filter(i => i.id !== inv.id));
      if (response === "accepted") {
        showToast(`✓ Confirmed — ${inv.raid_name} with ${inv.guild_name}`, "success");
      } else {
        showToast(`Declined invitation to ${inv.raid_name}`, "info");
      }
      onResponded?.();
    } catch {
      showToast("Failed to respond — please try again.", "error");
    } finally {
      setLoadingById(prev => ({ ...prev, [inv.id]: false }));
    }
  }, [showToast, onResponded]);

  if (invites.length === 0) return null;

  const isMobile = variant === "mobile";

  return (
    <div className="rounded-xl border border-purple-500/40 bg-purple-500/[0.07] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-purple-500/20 bg-purple-500/5">
        <svg className="w-4 h-4 text-purple-700 dark:text-purple-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <div className="flex flex-col min-w-0">
          <span className="text-purple-700 dark:text-purple-300 font-bold text-xs uppercase tracking-wider">
            {invites.length === 1 ? "Raid Invitation" : `Raid Invitations (${invites.length})`}
          </span>
          <span className="text-[10px] text-purple-700/60 dark:text-purple-300/60">
            You've been invited to raid with another guild
          </span>
        </div>
      </div>
      <div className="divide-y divide-purple-500/10">
        {invites.map(inv => {
          const loading    = !!loadingById[inv.id];
          const dateStr    = formatStartsAt(inv.starts_at);
          const logoUrl    = resolveLogo(inv.guild_logo_url);
          const classColor = inv.class_name ? (CLASS_COLORS[inv.class_name] || "#fff") : "#fff";
          return (
            <div key={inv.id} className={`flex ${isMobile ? "flex-col" : "flex-col sm:flex-row sm:items-center"} gap-3 px-4 py-4`}>
              {/* Guild logo */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-[var(--c-sunken)] border border-[var(--c-line)] shrink-0 flex items-center justify-center">
                  {logoUrl
                    ? <img src={logoUrl} alt={inv.guild_name} className="w-full h-full object-cover" />
                    : <span className="text-lg font-black text-[var(--c-ink-4)]">{inv.guild_name.charAt(0)}</span>
                  }
                </div>
                {isMobile && (
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[var(--c-ink)] truncate">{inv.raid_name}</div>
                    <div className="text-[11px] text-purple-700 dark:text-purple-300 truncate">
                      with <strong>{inv.guild_name}</strong>{inv.guild_tag ? ` [${inv.guild_tag}]` : ""}
                    </div>
                  </div>
                )}
              </div>

              {/* Info block — hidden on mobile (shown beside logo above) */}
              {!isMobile && (
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold text-[var(--c-ink)]">{inv.raid_name}</span>
                    <span className="text-xs text-purple-700 dark:text-purple-300">
                      with <strong>{inv.guild_name}</strong>{inv.guild_tag ? ` [${inv.guild_tag}]` : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    {inv.character_name && (
                      <span className="text-xs text-[var(--c-ink-3)]">
                        Your character:&nbsp;
                        <span className="font-medium" style={{ color: classColor }}>{inv.character_name}</span>
                        {inv.class_name ? <span className="text-[var(--c-ink-3)]"> · {inv.class_name}</span> : null}
                      </span>
                    )}
                    {dateStr && <span className="text-xs text-[var(--c-ink-3)]">📅 {dateStr}</span>}
                    {inv.raid_type_label && <span className="text-xs text-[var(--c-ink-4)]">{inv.raid_type_label}</span>}
                  </div>
                  {inv.loot_info_sent && (
                    <details className="mt-1.5">
                      <summary className="text-[11px] text-purple-600/70 dark:text-purple-400/70 cursor-pointer hover:text-purple-700 dark:hover:text-purple-300 transition-colors select-none">
                        View loot rules
                      </summary>
                      <p className="mt-1 text-xs text-[var(--c-ink-2)] bg-[var(--c-surface)] rounded px-2 py-1.5 border border-[var(--c-line-soft)] whitespace-pre-wrap">
                        {inv.loot_info_sent}
                      </p>
                    </details>
                  )}
                </div>
              )}

              {/* Mobile: secondary info row */}
              {isMobile && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                  {inv.character_name && (
                    <span className="text-[var(--c-ink-3)]">
                      Character:&nbsp;
                      <span className="font-medium" style={{ color: classColor }}>{inv.character_name}</span>
                    </span>
                  )}
                  {dateStr && <span className="text-[var(--c-ink-3)]">📅 {dateStr}</span>}
                </div>
              )}
              {isMobile && inv.loot_info_sent && (
                <details>
                  <summary className="text-[11px] text-purple-600/70 dark:text-purple-400/70 cursor-pointer hover:text-purple-700 dark:hover:text-purple-300 transition-colors select-none">
                    View loot rules
                  </summary>
                  <p className="mt-1 text-xs text-[var(--c-ink-2)] bg-[var(--c-surface)] rounded px-2 py-1.5 border border-[var(--c-line-soft)] whitespace-pre-wrap">
                    {inv.loot_info_sent}
                  </p>
                </details>
              )}

              {/* Buttons */}
              <div className={`flex items-center gap-2 shrink-0 ${isMobile ? "mt-1" : ""}`}>
                <button
                  onClick={() => respond(inv, "declined")}
                  disabled={loading}
                  className="h-9 px-4 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 border border-red-500/25 text-xs font-bold transition-all disabled:opacity-50 flex-1 sm:flex-none"
                >
                  {loading ? "…" : "Decline"}
                </button>
                <button
                  onClick={() => respond(inv, "accepted")}
                  disabled={loading}
                  className="h-9 px-5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-lg shadow-green-900/20 flex-1 sm:flex-none"
                >
                  {loading ? "…" : "✓ Accept"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}